// Invoice Form Component
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Save,
  Cancel,
  AddCircle,
  RemoveCircle,
  Receipt,
  Person,
  Phone,
  CalendarToday,
  Percent,
  Search,
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import apiService from '../../services/api';
import authService from '../../services/auth';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For editing existing invoice
  const { formatPrice } = useCurrency();
  const { getCurrentDate } = useTimezone();
  const todayISO = getCurrentDate(); // YYYY-MM-DD in tenant timezone (defaults to Asia/Kolkata)
  const tenantId = authService.getUser?.()?.tenant_id || authService.getTenant?.()?.id;

  const normalizeGenderShort = (genderRaw) => {
    if (!genderRaw) return '';
    const g = String(genderRaw).trim().toLowerCase();
    if (g === 'male' || g === 'm' || g === 'men' || g === 'man' || g === 'boy') return 'M';
    if (g === 'female' || g === 'f' || g === 'women' || g === 'woman' || g === 'girl') return 'W';
    if (g.startsWith('m')) return 'M';
    if (g.startsWith('f') || g.startsWith('w')) return 'W';
    return '';
  };

  const isWomenGender = (genderRaw) => normalizeGenderShort(genderRaw) === 'W';

  const formatMoneySafe = (value) => {
    const n = Number(value);
    return formatPrice(Number.isFinite(n) ? n : 0);
  };

  const normalizeForSearch = (value) =>
    String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const computeMembershipEndDate = (membershipPlan, startDateISO) => {
    if (!membershipPlan) return null;
    const durationType = String(membershipPlan?.duration_type ?? membershipPlan?.durationType ?? '').toLowerCase();
    if (!startDateISO) return null;
    if (durationType === 'lifetime') return null;

    const monthsRaw =
      membershipPlan?.duration_months ??
      membershipPlan?.durationMonths ??
      membershipPlan?.duration ??
      null;
    const months = Number(monthsRaw);
    if (!Number.isFinite(months) || months <= 0) return null;

    const end = dayjs(startDateISO).add(months, 'month');
    return end.isValid() ? end.format('YYYY-MM-DD') : null;
  };

  const getServiceCategoryText = (service) => {
    const category =
      service?.category_name ??
      service?.category ??
      service?.service_category ??
      '';
    const subCategory =
      service?.sub_category_name ??
      service?.subcategory_name ??
      service?.sub_category ??
      service?.subCategory ??
      '';
    const c = String(category || '').trim();
    const sc = String(subCategory || '').trim();
    if (c && sc) return `${c} • ${sc}`;
    return c || sc || '';
  };

  // Custom filter: match ALL typed words (supports partials like "adv").
  // Uses our controlled input state to avoid any MUI internal edge-cases.
  const filterItemOptions = React.useCallback((options, inputValueRaw) => {
    const q = normalizeForSearch(inputValueRaw);
    if (!q) return options;
    const words = q.split(/\s+/).filter(Boolean);
    if (!words.length) return options;
    return options.filter((option) => {
      const name = option?.name ?? '';
      const hay = normalizeForSearch(name);
      return words.every((w) => hay.includes(w));
    });
  }, []);

  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [clients, setClients] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [activeMembership, setActiveMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState(() => ({
    client_mobile: '',
    client_name: '',
    client_id: '',
    invoice_number: '',
    invoice_date: todayISO,
    status: 'submitted',
    discount_type: 'amount',
    discount_value: 0,
    notes: '',
    items: [{ type: 'service', item_id: '', name: '', quantity: 1, price: 0, gst_percentage: 0, stylist_id: '' }],
  }));

  const [payments, setPayments] = useState(() => ([
    { payment_date: todayISO, payment_mode: 'cash', amount: 0, reference_number: '', notes: '' }
  ]));

  const [clientFound, setClientFound] = useState(false);
  const [clientNameReadonly, setClientNameReadonly] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [itemSearchInputs, setItemSearchInputs] = useState([]);

  // Services dropdown order: Men (A→Z) first, then Women (A→Z), then others.
  const sortedServices = React.useMemo(() => {
    const arr = Array.isArray(services) ? [...services] : [];
    arr.sort((a, b) => {
      const ga = normalizeGenderShort(a?.gender);
      const gb = normalizeGenderShort(b?.gender);
      const groupA = ga === 'M' ? 0 : ga === 'W' ? 1 : 2;
      const groupB = gb === 'M' ? 0 : gb === 'W' ? 1 : 2;
      if (groupA !== groupB) return groupA - groupB;

      const na = String(a?.name ?? '').toLowerCase();
      const nb = String(b?.name ?? '').toLowerCase();
      if (na < nb) return -1;
      if (na > nb) return 1;

      const ia = String(a?.id ?? '');
      const ib = String(b?.id ?? '');
      return ia.localeCompare(ib);
    });
    return arr;
  }, [services]);

  const paymentModes = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];

  useEffect(() => {
    fetchData();
    // `fetchData` is defined in-component and is stable for our usage here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep per-row Autocomplete input values in sync with rows
  useEffect(() => {
    setItemSearchInputs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const targetLen = formData.items.length;
      if (next.length < targetLen) {
        next.push(...Array(targetLen - next.length).fill(''));
      } else if (next.length > targetLen) {
        next.length = targetLen;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items.length]);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    } else {
      generateInvoiceNumber();
    }
    // `fetchInvoice` and `generateInvoiceNumber` are defined in-component and are stable for our usage here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesRes, productsRes, membershipsRes, clientsRes, stylistsRes] = await Promise.all([
        apiService.get('/tenant-services'),
        apiService.get('/products'),
        apiService.get('/memberships'),
        apiService.get('/clients', tenantId ? { tenant_id: tenantId } : {}),
        apiService.get('/stylists'),
      ]);

      if (servicesRes.success) {
        setServices(servicesRes.data.services || []);
      }
      if (productsRes.success) {
        setProducts(productsRes.data.products || []);
      }
      if (membershipsRes.success) {
        const list = Array.isArray(membershipsRes.data)
          ? membershipsRes.data
          : (membershipsRes.data?.memberships || []);
        setMemberships(Array.isArray(list) ? list : []);
      }
      if (clientsRes.success) {
        const list = clientsRes.data.clients || [];
        const filtered = tenantId
          ? list.filter(c => String(c?.tenant_id ?? '') === String(tenantId))
          : list;
        setClients(filtered);
      }
      if (stylistsRes.success) {
        const stylistList = Array.isArray(stylistsRes.data)
          ? stylistsRes.data
          : (stylistsRes.data?.stylists || []);
        setStylists(stylistList);
      }
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await apiService.get(`/invoices/${id}`);
      if (response.success) {
        const invoice = response.data.invoice;
        // Map items to include stylist_id
        const mappedItems = invoice.items ? invoice.items.map(item => ({
          ...item,
          stylist_id: item.stylist_id || ''
        })) : [{ type: 'service', item_id: '', name: '', quantity: 1, price: 0, gst_percentage: 0, stylist_id: '' }];

        setFormData({
          client_mobile: invoice.client_mobile || '',
          client_name: invoice.client_name || '',
          invoice_number: invoice.invoice_number,
          invoice_date: invoice.invoice_date,
          status: invoice.status,
          discount_type: invoice.discount_type || 'amount',
          discount_value: invoice.discount_value || 0,
          payment_mode1: invoice.payment_mode1 || 'cash',
          payment_mode2: invoice.payment_mode2 || '',
          payment_amount1: invoice.payment_amount1 || 0,
          payment_amount2: invoice.payment_amount2 || 0,
          notes: invoice.notes || '',
          items: mappedItems,
        });
        setClientFound(true);
        setClientNameReadonly(true);
      }
    } catch (err) {
      setError('Error fetching invoice: ' + err.message);
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const response = await apiService.get('/invoices/next-number');
      if (response.success) {
        const invoiceNumber =
          response?.data?.invoice_number ||
          response?.data?.next_invoice_number ||
          '';
        setFormData(prev => ({ ...prev, invoice_number: invoiceNumber }));
      } else {
        console.error('Failed to generate invoice number:', response.message);
      }
    } catch (err) {
      console.error('Error generating invoice number:', err);
    }
  };

  const searchClientByMobile = async (mobile) => {
    const mobileStr = String(mobile || '').trim();
    if (mobileStr.length < 10) {
      setActiveMembership(null);
      setClientFound(false);
      setClientNameReadonly(false);
      setFormData(prev => ({ ...prev, client_id: '' }));
      return;
    }

    try {
      // Always search server-side so results are tenant-scoped (ClientController filters by $user['tenant_id'])
      const res = await apiService.get('/clients', { search: mobileStr });
      const list = res?.success ? (res?.data?.clients || []) : [];
      const existingClient = list.find((client) => String(client?.mobile || '').trim() === mobileStr);

      if (existingClient) {
        setFormData(prev => ({
          ...prev,
          client_name: `${existingClient.first_name} ${existingClient.last_name}`.trim(),
          client_mobile: String(existingClient.mobile || '').trim(),
          client_id: existingClient.id
        }));
        setClientFound(true);
        setClientNameReadonly(true);
        
        // Fetch active membership for this client
        try {
          const membershipRes = await apiService.get(`/memberships/client/${existingClient.id}/active`);
          setActiveMembership(membershipRes?.success ? (membershipRes?.data?.membership || null) : null);
        } catch (err) {
          console.error('Error fetching active membership:', err);
          setActiveMembership(null);
        }
      } else {
        setClientFound(false);
        setClientNameReadonly(false);
        setActiveMembership(null);
        setFormData(prev => ({ ...prev, client_id: '' }));
      }
    } catch (err) {
      console.error('Error searching client:', err);
      setActiveMembership(null);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-search client when mobile number changes
    if (field === 'client_mobile') {
      searchClientByMobile(value);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Check if client already has an active membership when trying to add/select membership
    if (field === 'type' && value === 'membership') {
      if (activeMembership && activeMembership.status === 'active') {
        const isLifetime = !activeMembership.end_date;
        const message = isLifetime
          ? `Client already has an active lifetime membership (${activeMembership.membership_name}). Cannot add another membership.`
          : `Client already has an active membership (${activeMembership.membership_name}) valid until ${new Date(activeMembership.end_date).toLocaleDateString()}. Cannot add another membership.`;
        setError(message);
        // Revert the type change
        newItems[index] = { ...newItems[index], type: 'service' };
        setFormData(prev => ({ ...prev, items: newItems }));
        return;
      }
    }

    // Auto-populate item details when item_id changes
    if (field === 'item_id') {
      const item = newItems[index];
      
      // Check if trying to select a membership when client already has one
      if (item.type === 'membership' && activeMembership && activeMembership.status === 'active') {
        const isLifetime = !activeMembership.end_date;
        const message = isLifetime
          ? `Client already has an active lifetime membership (${activeMembership.membership_name}). Cannot add another membership.`
          : `Client already has an active membership (${activeMembership.membership_name}) valid until ${new Date(activeMembership.end_date).toLocaleDateString()}. Cannot add another membership.`;
        setError(message);
        // Clear the item_id selection
        newItems[index] = { ...newItems[index], item_id: '', name: '', price: 0 };
        setFormData(prev => ({ ...prev, items: newItems }));
        return;
      }
      
      if (item.type === 'service') {
        const service = services.find(s => String(s.id) === String(value));
        if (service) {
          const memberPriceRaw = service.member_price ?? service.memberprice;
          const memberPriceNum = Number(memberPriceRaw);
          const hasMemberPrice =
            memberPriceRaw !== null &&
            memberPriceRaw !== undefined &&
            String(memberPriceRaw).trim() !== '' &&
            Number.isFinite(memberPriceNum);
          const hasActiveMembership =
            !!(activeMembership && String(activeMembership.status).toLowerCase() === 'active');

          newItems[index] = {
            ...newItems[index],
            name: service.name,
            price: hasActiveMembership && hasMemberPrice ? memberPriceNum : service.price,
            gst_percentage: service.gst_percentage || 0
          };

        }
      } else if (item.type === 'product') {
        const product = products.find(p => String(p.id) === String(value));
        if (product) {
          newItems[index] = {
            ...newItems[index],
            name: product.name,
            price: product.price,
            gst_percentage: product.gst_percentage || 0
          };

        }
      } else if (item.type === 'membership') {
        const membershipList = Array.isArray(memberships) ? memberships : [];
        const membership = membershipList.find(m => String(m.id) === String(value));
        if (membership) {
          newItems[index] = {
            ...newItems[index],
            name: membership.name,
            price: membership.price,
            gst_percentage: 0 // Memberships typically don't have GST
          };
        }
      }
    }

    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { type: 'service', item_id: '', name: '', quantity: 1, price: 0, gst_percentage: 0, stylist_id: '' }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const addPayment = () => {
    setPayments(prev => [...prev, { 
      payment_date: getCurrentDate(), 
      payment_mode: 'cash', 
      amount: 0, 
      reference_number: '', 
      notes: '' 
    }]);
  };

  const removePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaymentChange = (index, field, value) => {
    setPayments(prev => {
      const newPayments = [...prev];
      newPayments[index] = { ...newPayments[index], [field]: value };
      return newPayments;
    });
  };

  // Calculate row amount for each item
  const calculateRowAmount = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price) || 0;
    const gstPercentage = parseFloat(item.gst_percentage) || 0;
    const baseAmount = quantity * price;
    if (gstPercentage > 0) {
      return baseAmount + (baseAmount * (gstPercentage / 100));
    }
    return baseAmount;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let subtotalForDiscount = 0; // Exclude membership items from discount calculation

    // Calculate subtotal first
    formData.items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const itemTotal = quantity * price;
      subtotal += itemTotal;
      
      // Exclude membership items from discount calculation
      if (item.type !== 'membership') {
        subtotalForDiscount += itemTotal;
      }
    });

    // Calculate regular discount
    const discountValue = parseFloat(formData.discount_value) || 0;
    const discountAmount = formData.discount_type === 'percentage'
      ? subtotalForDiscount * (discountValue / 100)
      : discountValue;

    let discountedTaxableAmount = subtotalForDiscount - discountAmount;

    // Apply membership discount if client has active membership
    let membershipDiscountAmount = 0;
    if (activeMembership && activeMembership.discount_percentage > 0 && discountedTaxableAmount > 0) {
      membershipDiscountAmount = discountedTaxableAmount * (activeMembership.discount_percentage / 100);
      discountedTaxableAmount = discountedTaxableAmount - membershipDiscountAmount;
    }

    // Calculate GST on taxable amount only (after discounts, excluding membership items)
    let gstAmount = 0;
    formData.items.forEach(item => {
      // Skip membership items for GST calculation
      if (item.type === 'membership') {
        return;
      }
      
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const gstPercentage = parseFloat(item.gst_percentage) || 0;

      if (gstPercentage > 0) {
        const itemTotal = quantity * price;
        // Calculate proportional GST based on discounted taxable amount
        const itemProportion = subtotalForDiscount > 0 ? itemTotal / subtotalForDiscount : 0;
        const itemNetAmount = discountedTaxableAmount * itemProportion;
        const itemGst = itemNetAmount * (gstPercentage / 100);
        gstAmount += itemGst;
      }
    });

    // Add membership items to total
    let membershipItemsTotal = 0;
    formData.items.forEach(item => {
      if (item.type === 'membership') {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        membershipItemsTotal += quantity * price;
      }
    });

    // Net Amount should include membership line items (non-taxable in current flow).
    const netAmount = discountedTaxableAmount + membershipItemsTotal;
    const totalAmount = netAmount + gstAmount;

    return {
      subtotal,
      discountAmount,
      membershipDiscountAmount,
      netAmount,
      gstAmount,
      totalAmount
    };
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate required fields
      if (!formData.client_mobile || !formData.client_name || !formData.invoice_date) {
        setError('Please fill in all required fields');
        return;
      }

      if (formData.items.length === 0 || formData.items.every(item => !item.item_id)) {
        setError('Please add at least one item to the invoice');
        return;
      }

      // Validate payment amounts
      const { totalAmount } = calculateTotals();
      const totalPayment = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
      if (totalPayment > totalAmount) {
        setError('Total payment amount cannot exceed invoice total');
        return;
      }

      // Ensure we have a client_id. If not, create a minimal client record.
      let clientId = formData.client_id;
      if (!clientId) {
        const mobile = String(formData.client_mobile || '').trim();
        const name = String(formData.client_name || '').trim();

        const existingClient = clients.find((c) => String(c.mobile) === mobile);
        if (existingClient?.id) {
          clientId = existingClient.id;
        } else {
          const parts = name.split(' ').filter(Boolean);
          const first_name = parts[0] || name || 'Client';
          const last_name = parts.slice(1).join(' ');
          const createRes = await apiService.post('/clients', {
            first_name,
            last_name,
            mobile,
            status: 'active',
            ...(tenantId ? { tenant_id: tenantId } : {}),
          });
          if (createRes?.success && createRes?.data?.id) {
            clientId = createRes.data.id;
          } else {
            setError(createRes?.message || 'Failed to create client');
            return;
          }
        }
      }

      const totals = calculateTotals();

      const mappedItems = formData.items
        .filter(item => item.item_id)
        .map(item => {
          const quantity = parseFloat(item.quantity) || 0;
          const unit_price = parseFloat(item.price) || 0;
          const gst_percentage = parseFloat(item.gst_percentage) || 0;
          const base = quantity * unit_price;
          const gst_amount = gst_percentage > 0 ? base * (gst_percentage / 100) : 0;
          const total_price = base + gst_amount;

          return {
            item_type: item.type,
            item_id: item.item_id,
            item_name: item.name,
            quantity,
            unit_price,
            total_price,
            gst_percentage,
            gst_amount,
            stylist_id: item.stylist_id === '' ? null : item.stylist_id,
            price_inclusive_gst: 'false',
          };
        });

      const submitData = {
        client_id: clientId,
        client_mobile: String(formData.client_mobile || '').trim(),
        client_name: String(formData.client_name || '').trim(),
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date,
        status: 'submitted',
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value) || 0,
        subtotal: totals.subtotal,
        discount_amount: totals.discountAmount,
        membership_discount_amount: totals.membershipDiscountAmount,
        net_amount: totals.netAmount,
        gst_amount: totals.gstAmount,
        total_amount: totals.totalAmount,
        notes: formData.notes,
        items: mappedItems,
        payments: payments.filter(payment => (parseFloat(payment.amount) || 0) > 0),
      };

      let response;
      if (id) {
        response = await apiService.put(`/invoices/${id}`, submitData);
      } else {
        response = await apiService.post('/invoices', submitData);
      }

      if (response.success) {
        // If invoice includes membership purchase(s), assign to client_memberships table.
        const membershipItems = mappedItems.filter((it) => it.item_type === 'membership' && it.item_id);
        if (membershipItems.length > 0) {
          try {
            for (const mi of membershipItems) {
              const plan = (Array.isArray(memberships) ? memberships : []).find(
                (m) => String(m?.id) === String(mi.item_id)
              );
              const startDate = submitData.invoice_date;
              const endDate = computeMembershipEndDate(plan, startDate);
              const firstPayment = payments?.[0] || null;
              const paymentMode = firstPayment?.payment_mode || null;
              const paymentAmount = Number(mi.total_price ?? (Number(mi.quantity) * Number(mi.unit_price)));

              await apiService.post(`/clients/${clientId}/memberships`, {
                membership_id: mi.item_id,
                start_date: startDate,
                end_date: endDate,
                status: 'active',
                payment_amount: Number.isFinite(paymentAmount) ? paymentAmount : null,
                payment_mode: paymentMode,
                notes: `Assigned via invoice ${submitData.invoice_number || ''}`.trim(),
              });
            }
          } catch (assignErr) {
            setError(
              `Invoice saved, but membership assignment failed: ${assignErr?.message || 'Unknown error'}`
            );
            return;
          }
        }

        setSuccess(id ? 'Invoice updated successfully!' : 'Invoice created successfully!');
        setTimeout(() => {
          navigate('/dashboard/invoices');
        }, 1500);
      } else {
        setError(response.message || 'Failed to save invoice');
      }
    } catch (err) {
      const fieldErrors = err?.responseData?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const msg = Object.entries(fieldErrors)
          .map(([k, v]) => `${k}: ${v}`)
          .slice(0, 10)
          .join('\n');
        setError(msg || ('Error saving invoice: ' + (err.message || 'Unknown error')));
      } else {
        setError('Error saving invoice: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/invoices');
  };

  const { subtotal, discountAmount, membershipDiscountAmount, netAmount, gstAmount, totalAmount } = calculateTotals();



  // Auto-fill first payment amount with total when total changes
  useEffect(() => {
    // Always reset first payment amount when total changes (items added/removed)
    setPayments(prev => {
      const newPayments = [...prev];
      if (newPayments.length > 0) {
        newPayments[0] = { ...newPayments[0], amount: totalAmount };
      }
      return newPayments;
    });
    
    // Mark as no longer initial load after first calculation
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [totalAmount, isInitialLoad]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Receipt sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
            {id ? 'Edit Invoice' : 'Create New Invoice'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid  spacing={2} sx={{ display: 'flex', gap: 2  }}>
        {/* Left Column - Invoice Details */}
        <Grid item xs={12} lg={8} style={{  flex: 2 }}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              {/* Invoice Header */}
              <Grid spacing={2} sx={{ mb: 2 }}>
                <Grid item md={12} sm={12} style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Invoice Number"
                    value={formData.invoice_number}
                    onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                    required
                    InputProps={{
                      startAdornment: <Receipt sx={{ fontSize: 16 }} />,
                    }}
                    helperText="Auto-generated, but can be modified if needed"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Invoice Date"
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                    required
                    inputProps={{ max: todayISO }}
                    InputProps={{
                      startAdornment: <CalendarToday sx={{ fontSize: 16 }} />,
                    }}
                  />
                </Grid>
              </Grid>

              {/* Client Information */}
              <Grid spacing={2} sx={{ mb: 2 }}>
                <Grid item md={12} sm={12} style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Mobile Number"
                    value={formData.client_mobile}
                    onChange={(e) => handleInputChange('client_mobile', e.target.value)}
                    required
                    InputProps={{
                      startAdornment: <Phone sx={{ fontSize: 16 }} />,
                    }}
                    helperText={clientFound ? "Client found" : "New client"}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Client Name"
                    value={formData.client_name}
                    onChange={(e) => handleInputChange('client_name', e.target.value)}
                    required
                    disabled={clientNameReadonly}
                    InputProps={{
                      startAdornment: <Person sx={{ fontSize: 16 }} />,
                    }}
                  />
                </Grid>
              </Grid>



              {/* Invoice Items Table */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Invoice Items
              </Typography>

              {/* Invoice Items - Two Line Layout */}
              {formData.items.map((item, index) => (
                <Card key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
                  {/* First Row - Type and Stylist */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      width: '100%',
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={item.type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            
                            // Prevent changing to membership if client already has an active membership
                            if (newType === 'membership' && activeMembership && activeMembership.status === 'active') {
                              const isLifetime = !activeMembership.end_date;
                              const message = isLifetime
                                ? `Client already has an active lifetime membership (${activeMembership.membership_name}). Cannot add another membership.`
                                : `Client already has an active membership (${activeMembership.membership_name}) valid until ${new Date(activeMembership.end_date).toLocaleDateString()}. Cannot add another membership.`;
                              setError(message);
                              return;
                            }
                            
                            const newItems = [...formData.items];
                            // Reset all item fields when type changes
                            newItems[index] = {
                              ...newItems[index],
                              type: newType,
                              item_id: '',
                              name: '',
                              price: 0,
                              gst_percentage: 0,
                              stylist_id: newType === 'service' ? newItems[index].stylist_id : ''
                            };
                            setFormData(prev => ({ ...prev, items: newItems }));
                            setItemSearchInputs((prev) => {
                              const next = Array.isArray(prev) ? [...prev] : [];
                              next[index] = '';
                              return next;
                            });
                          }}
                          label="Type"
                        >
                          <MenuItem value="service">Service</MenuItem>
                          <MenuItem value="product">Product</MenuItem>
                          <MenuItem 
                            value="membership"
                            disabled={activeMembership && activeMembership.status === 'active'}
                          >
                            Membership
                            {activeMembership && activeMembership.status === 'active' && ' (Client already has active membership)'}
                          </MenuItem>
                        </Select>
                      </FormControl> 
                    </Box>
                    <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Stylist</InputLabel>
                        <Select
                          value={item.stylist_id}
                          onChange={(e) => handleItemChange(index, 'stylist_id', e.target.value)}
                          label="Stylist"
                        >
                          <MenuItem value="">
                            <em>Select stylist</em>
                          </MenuItem>
                          {stylists.map((stylist) => (
                            <MenuItem key={stylist.id} value={stylist.id}>
                              {stylist.first_name} {stylist.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Second Row - Search and Select Item (forced to next line) */}
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={filterItemOptions(
                        item.type === 'service' ? sortedServices :
                        item.type === 'product' ? products :
                        item.type === 'membership' ? memberships : [],
                        itemSearchInputs[index] ?? ''
                      )}
                      inputValue={itemSearchInputs[index] ?? ''}
                      onInputChange={(event, newInputValue) => {
                        setItemSearchInputs((prev) => {
                          const next = Array.isArray(prev) ? [...prev] : [];
                          next[index] = newInputValue ?? '';
                          return next;
                        });
                      }}
                      // We already filter via `options=...`, so keep MUI from filtering again.
                      filterOptions={(options) => options}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.id) === String(value?.id)
                      }
                      getOptionLabel={(option) => {
                        if (!option?.name) return '';
                        const base = `${option.name} - ${formatMoneySafe(option.price)}`;
                        if (item.type === 'service') {
                          const genderShort = normalizeGenderShort(option.gender);
                          const memberPriceOk =
                            option.member_price !== null &&
                            option.member_price !== undefined &&
                            String(option.member_price).trim() !== '';
                          const memberStr = memberPriceOk ? ` - ${formatMoneySafe(option.member_price)} (M)` : '';
                          const genderStr = genderShort ? ` (${genderShort})` : '';
                          return `${option.name}${genderStr} - ${formatMoneySafe(option.price)}${memberStr}`;
                        }
                        return base;
                      }}
                      value={
                        item.type === 'service'
                          ? services.find(s => String(s.id) === String(item.item_id)) || null
                          : item.type === 'product'
                          ? products.find(p => String(p.id) === String(item.item_id)) || null
                          : item.type === 'membership'
                          ? (Array.isArray(memberships) ? memberships : []).find(m => String(m.id) === String(item.item_id)) || null
                          : null
                      }
                      onChange={(event, newValue) => {
                        handleItemChange(index, 'item_id', newValue ? newValue.id : '');
                      }}
                      renderOption={(props, option) => {
                        const optionIndex = props['data-option-index'];
                        const { key, ...liProps } = props;
                        const uniqueKey =
                          option?.id !== null && option?.id !== undefined && String(option?.id).trim() !== ''
                            ? String(option.id)
                            : `${String(key)}-${optionIndex}`;

                        if (item.type !== 'service') {
                          return (
                            <li key={uniqueKey} {...liProps}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                <Typography sx={{ fontWeight: 500 }}>{option?.name || ''}</Typography>
                                <Typography sx={{ fontWeight: 600 }}>{formatMoneySafe(option?.price)}</Typography>
                              </Box>
                            </li>
                          );
                        }

                        const genderShort = normalizeGenderShort(option?.gender);
                        const categoryText = getServiceCategoryText(option);
                        const memberPriceOk =
                          option?.member_price !== null &&
                          option?.member_price !== undefined &&
                          String(option?.member_price).trim() !== '';
                        const womenRow = isWomenGender(option?.gender);

                        return (
                          <li key={uniqueKey} {...liProps}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 2,
                                color: womenRow ? '#e91e63' : 'inherit',
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      color: 'inherit',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {option?.name || ''}
                                  </Typography>
                                  {genderShort && (
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: womenRow ? 'inherit' : 'text.secondary',
                                      }}
                                    >
                                      {genderShort}
                                    </Typography>
                                  )}
                                </Box>
                                {categoryText && (
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: womenRow ? 'inherit' : 'text.secondary',
                                      opacity: womenRow ? 0.8 : 1,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {categoryText}
                                  </Typography>
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                <Typography sx={{ fontWeight: 600, color: 'inherit' }}>{formatMoneySafe(option?.price)}</Typography>
                                {memberPriceOk && (
                                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'inherit' }}>
                                    {formatMoneySafe(option?.member_price)} (M)
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search and Select Item"
                          placeholder="Type to search items..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Search sx={{ fontSize: 16 }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* Second Row - Qty, Price, GST, Row Amount, Action */}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                      <TextField
                        size="small"
                        label="Qty"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 1, step: 1 }}
                        fullWidth
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        label="Price"
                        type="number"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        fullWidth
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        size="small"
                        label="GST %"
                        type="number"
                        value={item.gst_percentage}
                        onChange={(e) => handleItemChange(index, 'gst_percentage', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        InputProps={{
                          endAdornment: <Percent sx={{ fontSize: 16 }} />,
                        }}
                        fullWidth
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{
                        p: 1.5,
                        backgroundColor: 'primary.light',
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                         ₹{calculateRowAmount(item).toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(index)}
                        disabled={formData.items.length === 1}
                        sx={{
                          border: '1px solid #f44336',
                          '&:hover': { backgroundColor: '#ffebee' }
                        }}
                      >
                        <RemoveCircle />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Button
                startIcon={<AddCircle />}
                onClick={addItem}
                variant="outlined"
                size="small"
              >
                Add Item
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary and Settings */}
        <Grid item xs={12} lg={4} style={{  flex: 1 }}>
          {/* Status and Discount */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Status & Discount
              </Typography>

              <RadioGroup
                value={formData.discount_type}
                onChange={(e) => handleInputChange('discount_type', e.target.value)}
                row
                sx={{ mb: 1 }}
              >
                <FormControlLabel value="amount" control={<Radio size="small" />} label="Amount" />
                <FormControlLabel value="percentage" control={<Radio size="small" />} label="%" />
              </RadioGroup>

              <TextField
                fullWidth
                size="small"
                label={`Discount ${formData.discount_type === 'percentage' ? '(%)' : '(₹)'}`}
                type="number"
                value={formData.discount_value}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string, only convert to number if there's a value
                  handleInputChange('discount_value', value === '' ? '' : parseFloat(value) || 0);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.discount_type === 'percentage' ? <Percent /> : <InputAdornment position="start">₹</InputAdornment>}
                    </InputAdornment>
                  ),
                }}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Payment Details
              </Typography>

              {payments.map((payment, index) => (
                <Card key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        label="Payment Date"
                        type="date"
                        value={payment.payment_date}
                        onChange={(e) => handlePaymentChange(index, 'payment_date', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                          value={payment.payment_mode}
                          onChange={(e) => handlePaymentChange(index, 'payment_mode', e.target.value)}
                          label="Payment Mode"
                        >
                          {paymentModes.map((mode) => (
                            <MenuItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        label="Amount"
                        type="number"
                        value={payment.amount}
                        onChange={(e) => handlePaymentChange(index, 'amount', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        fullWidth
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton
                        color="error"
                        onClick={() => removePayment(index)}
                        disabled={payments.length === 1}
                        sx={{ 
                          border: '1px solid #f44336',
                          '&:hover': { backgroundColor: '#ffebee' }
                        }}
                      >
                        <RemoveCircle />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Button
                startIcon={<AddCircle />}
                onClick={addPayment}
                variant="outlined"
                size="small"
              >
                Add Payment
              </Button>
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Invoice Summary
              </Typography>

              <Box sx={{ mb: 2 }}>
                {activeMembership && activeMembership.discount_percentage > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Active Membership: {activeMembership.membership_name} ({activeMembership.discount_percentage}% discount)
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">₹{subtotal.toFixed(2)}</Typography>
                </Box>
                {discountAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Discount:</Typography>
                    <Typography variant="body2" color="error">-₹{(parseFloat(discountAmount) || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                {membershipDiscountAmount > 0 && activeMembership?.discount_percentage > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Membership Discount ({activeMembership?.discount_percentage}%):</Typography>
                    <Typography variant="body2" color="success.main">-₹{(parseFloat(membershipDiscountAmount) || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Net Amount:</Typography>
                  <Typography variant="body2">₹{netAmount.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">GST:</Typography>
                  <Typography variant="body2">₹{gstAmount.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold">₹{totalAmount.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Paid:</Typography>
                  <Typography variant="body2" color="success.main">₹{payments.reduce((sum, payment) => sum + (payment.amount || 0), 0).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Balance:</Typography>
                  <Typography variant="body2" color={totalAmount - payments.reduce((sum, payment) => sum + (payment.amount || 0), 0) > 0 ? 'error' : 'success'}>
                    ₹{(totalAmount - payments.reduce((sum, payment) => sum + (payment.amount || 0), 0)).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notes */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Additional Notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Enter any additional notes for this invoice..."
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Cancel />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : (id ? 'Update' : 'Create')} Invoice
        </Button>
      </Box>
    </Box>
  );
};

export default InvoiceForm; 